import { useState } from 'react'
import { SlidersHorizontal } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'

interface ReportFilterBarProps {
  /** What the filters currently say, in one line: `14 T · Ganztags`. */
  summary: string
  children: React.ReactNode
}

/**
 * The filter row, one row on every screen (CHG-2026-09-manager-reporting).
 *
 * On a desktop the controls sit inline above what they scope. On a phone the same controls stack
 * to five rows and push the first figure below the fold — so there they collapse into a single
 * button that states the current slice and opens them in a sheet. The controls themselves are the
 * same components either way: this decides where they are shown, never what they do, so the two
 * layouts cannot drift apart.
 */
export function ReportFilterBar({ summary, children }: ReportFilterBarProps) {
  const { t } = useTranslation('reports')
  const [open, setOpen] = useState(false)

  return (
    <>
      <div
        className="hidden flex-wrap items-center gap-2 lg:flex"
        data-print-hide
      >
        {children}
      </div>

      <div className="lg:hidden" data-print-hide>
        <Button
          variant="outline"
          className="h-12 w-full justify-between gap-2"
          onClick={() => setOpen(true)}
        >
          <span className="truncate font-normal">{summary}</span>
          <SlidersHorizontal
            className="h-4 w-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        </Button>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
            <SheetHeader>
              <SheetTitle>{t('filterSheet.title')}</SheetTitle>
            </SheetHeader>
            <div className="flex flex-col items-start gap-4 px-4 pb-4">
              {children}
            </div>
            <div className="px-4 pb-6">
              <SheetClose asChild>
                <Button className="h-12 w-full">
                  {t('filterSheet.apply')}
                </Button>
              </SheetClose>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  )
}
